/**
 * Toteally Yours — Shop add-ons: patches, bag pins, stickers and bag charms on one page.
 * Extends the patches-only shop (patch-shop.js must load first) with a top-level
 * item-type filter. Each cart line keeps its own "Order type" so fulfillment,
 * the cart drawer and the app backend still see what each item is.
 * A type with nothing for sale shows that type's Coming Soon panel instead of a grid.
 */
const ADDONS_KIND_DEFAULTS = {
  patch: {
    orderType: 'Patches only',
    orderAttr: 'patches_only',
    itemProp: 'Patch',
    handleProp: 'Patch handle',
    countAttr: 'patch_count',
    one: 'patch',
    many: 'patches',
  },
  pin: {
    orderType: 'Bag pins only',
    orderAttr: 'bag_pins_only',
    itemProp: 'Bag pin',
    handleProp: 'Bag pin handle',
    countAttr: 'pin_count',
    one: 'bag pin',
    many: 'bag pins',
  },
  sticker: {
    orderType: 'Stickers only',
    orderAttr: 'stickers_only',
    itemProp: 'Sticker',
    handleProp: 'Sticker handle',
    countAttr: 'sticker_count',
    one: 'sticker',
    many: 'stickers',
  },
  charm: {
    orderType: 'Bag charms only',
    orderAttr: 'bag_charms_only',
    itemProp: 'Bag charm',
    handleProp: 'Bag charm handle',
    countAttr: 'charm_count',
    one: 'bag charm',
    many: 'bag charms',
  },
};

class TayAddonsShop extends TayPatchShop {
  connectedCallback() {
    const sectionId = this.getAttribute('data-section-id');
    const cfgEl = sectionId ? document.getElementById(`tay-patch-shop-config-${sectionId}`) : null;
    if (cfgEl instanceof HTMLScriptElement) {
      try {
        const raw = JSON.parse(cfgEl.textContent || '{}');
        // Flatten per-type groups into the single list the base shop works on.
        const groups = Array.isArray(raw.groups) ? raw.groups : [];
        raw.patches = [];
        const seen = new Set();
        groups.forEach((g) => {
          (g.products || []).forEach((p) => {
            if (!p || p.id == null || seen.has(Number(p.id))) return;
            seen.add(Number(p.id));
            p._kind = g.key;
            raw.patches.push(p);
          });
        });
        cfgEl.textContent = JSON.stringify(raw);
      } catch {
        /* base class falls back to an empty shop */
      }
    }
    /** @type {string | null} */
    this._kindFilter = null;
    this._loadingExtra = false;
    super.connectedCallback();
    if (!this._config) return;
    const fromUrl = new URLSearchParams(window.location.search).get('type');
    if (fromUrl && this.groupFor(fromUrl)) {
      this._kindFilter = fromUrl;
      this.render();
    }
  }

  groups() {
    return (this._config && Array.isArray(this._config.groups) && this._config.groups) || [];
  }

  /**
   * @param {string} key
   */
  groupFor(key) {
    return this.groups().find((g) => g.key === key) || null;
  }

  /**
   * @param {string} key
   */
  kindSettings(key) {
    const g = this.groupFor(key) || {};
    // Known kinds keep their historical labels; new kinds added in Shopify
    // (Add-on type entries) get labels derived from their own fields.
    const d = ADDONS_KIND_DEFAULTS[key] || {};
    const one = g.one || d.one || String(g.label || key || 'item').toLowerCase();
    const noun = one.charAt(0).toUpperCase() + one.slice(1);
    return {
      orderType: g.orderType || d.orderType || `${g.label || noun} only`,
      orderAttr: g.orderAttr || d.orderAttr || `${key}_only`,
      itemProp: g.itemProp || d.itemProp || noun,
      handleProp: g.handleProp || d.handleProp || `${noun} handle`,
      countAttr: d.countAttr || `${key}_count`,
      one,
      many: g.many || d.many || `${one}s`,
    };
  }

  /**
   * Runs fn with this._config temporarily pointed at one item type, so the base
   * shop's cart helpers write that type's line properties.
   * @param {any} product
   * @param {() => Promise<boolean>} fn
   */
  async withKind(product, fn) {
    const cfg = this._config;
    const ks = this.kindSettings(product && product._kind);
    const saved = {
      orderType: cfg.orderType,
      itemProp: cfg.itemProp,
      handleProp: cfg.handleProp,
    };
    cfg.orderType = ks.orderType;
    cfg.itemProp = ks.itemProp;
    cfg.handleProp = ks.handleProp;
    try {
      return await fn();
    } finally {
      Object.assign(cfg, saved);
    }
  }

  /**
   * @param {any} product
   */
  cartAddOne(product) {
    return this.withKind(product, () => super.cartAddOne(product));
  }

  /**
   * @param {any} product
   * @param {number} nextQty
   */
  cartSetQuantity(product, nextQty) {
    return this.withKind(product, () => super.cartSetQuantity(product, nextQty));
  }

  /**
   * @param {any} cart
   */
  applyPatchQtyFromCart(cart) {
    const types = new Set(this.groups().map((g) => this.kindSettings(g.key).orderType));
    /** @type {Record<string, number>} */
    const next = {};
    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    for (const item of items) {
      const props = item && item.properties ? item.properties : {};
      if (!types.has(String(props['Order type'] || ''))) continue;
      const id = String(item.variant_id || item.id || '');
      if (!id) continue;
      next[id] = (next[id] || 0) + Number(item.quantity || 0);
    }
    this._qty = next;
  }

  async loadExtraPatches() {
    const cfg = this._config;
    if (!cfg) return;
    const all = cfg.patches || [];
    const byId = new Map();
    all.forEach((p) => byId.set(Number(p.id), p));
    this._loadingExtra = true;
    try {
      for (const g of this.groups()) {
        cfg.patches = all.filter((p) => p._kind === g.key);
        cfg.collectionHandle = g.collectionHandle;
        cfg.allProductsCount = g.allProductsCount;
        await super.loadExtraPatches();
        cfg.patches.forEach((p) => {
          if (!p._kind) p._kind = g.key;
          if (!byId.has(Number(p.id))) byId.set(Number(p.id), p);
        });
      }
    } finally {
      this._loadingExtra = false;
      cfg.patches = Array.from(byId.values());
      cfg.collectionHandle = '';
      this.render();
      this.openFromQuery(true);
    }
  }

  categoryTags() {
    // Tag chips only make sense inside one item type.
    if (!this._kindFilter) return [];
    const cfg = this._config;
    const all = cfg.patches;
    cfg.patches = all.filter((p) => p._kind === this._kindFilter);
    try {
      return super.categoryTags();
    } finally {
      cfg.patches = all;
    }
  }

  filteredPatches() {
    const list = super.filteredPatches();
    if (!this._kindFilter) return list;
    return list.filter((p) => p._kind === this._kindFilter);
  }

  render() {
    if (this._loadingExtra) return;
    super.render();
    const cfg = this._config;
    if (!cfg || !cfg.hasCollection) return;
    const tools = this.querySelector('.bbs-patch-shop__tools');
    if (!(tools instanceof HTMLElement)) return;
    const labels = cfg.labels || {};
    const chip = (key, label) => {
      const selected = (this._kindFilter || '') === key;
      return `<button type="button" class="bbs-patch-shop__chip${selected ? ' is-selected' : ''}" data-action="kind" data-kind="${patchShopEscapeHtml(key)}" aria-pressed="${selected ? 'true' : 'false'}">${patchShopEscapeHtml(label)}</button>`;
    };
    const row = document.createElement('div');
    row.className = 'bbs-patch-shop__chips bbs-addons-shop__types';
    row.setAttribute('role', 'group');
    row.setAttribute('aria-label', labels.typeFilterLabel || 'Item type');
    row.innerHTML = [chip('', labels.typeAll || 'All'), ...this.groups().map((g) => chip(g.key, g.label))].join('');
    tools.prepend(row);
    // Tag chips only show once a type is picked; hide the lone "All" tag chip otherwise.
    const tagChips = tools.querySelector('.bbs-patch-shop__chips:not(.bbs-addons-shop__types)');
    if (tagChips instanceof HTMLElement) tagChips.hidden = !this._kindFilter || this.categoryTags().length === 0;
    this.renderComingSoon();
  }

  /**
   * @param {any[]=} list
   */
  renderGrid(list) {
    super.renderGrid(list);
    this.renderComingSoon();
  }

  /** Swap the grid for the selected type's Coming Soon panel when it has nothing for sale. */
  renderComingSoon() {
    const grid = this.querySelector('[data-ref="grid"]');
    const search = this.querySelector('.bbs-patch-shop__search');
    if (!(grid instanceof HTMLElement)) return;
    const g = this._kindFilter ? this.groupFor(this._kindFilter) : null;
    const hasItems = g ? (this._config.patches || []).some((p) => p._kind === g.key) : true;
    const showSoon = Boolean(g) && !g.forSale && !hasItems;
    if (search instanceof HTMLElement) search.hidden = showSoon;
    grid.classList.toggle('bbs-addons-shop__soon-host', showSoon);
    if (!showSoon) return;
    const tpl = document.getElementById(`tay-addons-soon-${this.getAttribute('data-section-id')}-${g.key}`);
    grid.innerHTML = tpl instanceof HTMLTemplateElement ? tpl.innerHTML : '';
  }

  /**
   * @param {Event} event
   */
  handleEvent(event) {
    const target = event.target;
    if (event.type === 'click' && target instanceof Element) {
      const kindEl = target.closest('[data-action="kind"]');
      if (kindEl instanceof HTMLElement) {
        const key = kindEl.getAttribute('data-kind') || '';
        this._kindFilter = key || null;
        this._filter = null;
        if (window.history && window.history.replaceState) {
          const url = new URL(window.location.href);
          if (key) url.searchParams.set('type', key);
          else url.searchParams.delete('type');
          window.history.replaceState({}, '', url);
        }
        this.render();
        return;
      }
    }
    super.handleEvent(event);
  }

  async startCheckout() {
    const cfg = this._config || {};
    const labels = cfg.labels || {};
    const lines = this.lineItems();
    if (!lines.length) {
      this._error = labels.needOneError || 'Add at least one item before checkout.';
      this.renderBar();
      return;
    }
    this._busy = true;
    this._error = '';
    this.renderBar();

    /** @type {Record<string, number>} */
    const counts = {};
    lines.forEach((line) => {
      const key = line.product._kind || 'patch';
      counts[key] = (counts[key] || 0) + line.quantity;
    });
    const kinds = Object.keys(counts);
    const totalQty = this.totalQuantity();
    /** @type {Record<string, string>} */
    const attributes = {
      // shop_mixed is what the app backend expects when more than one type is in the order.
      order_type: kinds.length === 1 ? this.kindSettings(kinds[0]).orderAttr : 'shop_mixed',
      design_id: patchShopDesignId(cfg),
      design_title:
        kinds.length === 1
          ? `${totalQty} ${totalQty === 1 ? this.kindSettings(kinds[0]).one : this.kindSettings(kinds[0]).many}`
          : `${totalQty} ${totalQty === 1 ? labels.barPatchOne || 'item' : labels.barPatchMany || 'items'}`,
    };
    kinds.forEach((key) => {
      attributes[this.kindSettings(key).countAttr] = String(counts[key]);
    });

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      });
      const checkoutPath = String(cfg.checkoutPath || '/checkout').trim() || '/checkout';
      window.location.assign(checkoutPath);
    } catch {
      this._busy = false;
      this._error = labels.checkoutError || 'Could not start checkout. Please try again.';
      this.renderBar();
    }
  }
}

if (!customElements.get('tay-addons-shop')) {
  customElements.define('tay-addons-shop', TayAddonsShop);
}
